import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Zap, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const PENDING_KEY = "contentos_pending_purchase";

export default function CreateProfile() {
  const [params] = useSearchParams();
  const purchaseId = params.get("purchase") || "";
  const tierParam = params.get("tier") || "";
  const [pending, setPending] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const { register, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { document.documentElement.classList.add("dark"); }, []);

  useEffect(() => {
    // Restore pending purchase from localStorage as a fallback
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (raw) setPending(JSON.parse(raw));
    } catch (err) {
      console.warn("Could not restore pending purchase", err);
    }
  }, []);

  // If already logged in, skip straight forward
  useEffect(() => {
    if (user) nav(user.onboarded ? "/app" : "/onboarding");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form.email, form.password, form.name || form.email.split("@")[0], purchaseId || undefined);
      localStorage.removeItem(PENDING_KEY);
      toast.success("Profile created — let's set up your brand");
      nav("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setBusy(false); }
  };

  const tier = tierParam || pending?.tier || "your plan";
  const price = pending?.price_usd;

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background text-foreground">
      <div className="hidden md:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="h-8 w-8 grid place-items-center bg-primary text-primary-foreground rounded">
            <Zap className="h-4 w-4" strokeWidth={2.5}/>
          </div>
          <span className="font-display font-black text-xl tracking-tight">ContentOS</span>
        </Link>
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-primary text-xs uppercase tracking-widest font-bold">
            <CheckCircle2 className="h-4 w-4"/> Payment accepted
          </div>
          <div className="mt-4 font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
            Welcome to<br/>ContentOS <span className="text-primary capitalize">{tier}</span>.
          </div>
          {price != null && (
            <div className="mt-4 font-mono text-sm text-muted-foreground">Billed ${price}/mo · via Paddle</div>
          )}
          <div className="mt-8 text-muted-foreground max-w-md leading-relaxed">
            Two minutes to set up your account, then a 30–60 min onboarding so we can learn your business like a marketing employee who's worked for you for a year.
          </div>
        </div>
        <div className="text-xs text-muted-foreground font-mono relative">Step 1 of 3 · Create profile</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Step 1 of 3</div>
          <h1 className="mt-2 font-display font-black text-3xl tracking-tight">Create your profile</h1>
          <p className="mt-2 text-sm text-muted-foreground">Use the same email you paid with — we'll link your subscription automatically.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="create-profile-form">
            <div>
              <Label className="text-xs uppercase tracking-widest">Your name</Label>
              <Input
                data-testid="input-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 h-11" required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Email</Label>
              <Input
                data-testid="input-email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 h-11" required
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Password</Label>
              <Input
                data-testid="input-password" type="password" minLength={8} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 h-11" required
              />
              <div className="mt-1 text-xs text-muted-foreground">At least 8 characters.</div>
            </div>

            <Button data-testid="btn-create-profile" type="submit" disabled={busy} className="w-full h-11 mt-2">
              {busy ? "Working…" : "Create profile & continue"}
              <ArrowRight className="h-4 w-4" />
            </Button>

            <div className="text-xs text-muted-foreground mt-3 text-center">
              By creating your account you agree to the{" "}
              <Link to="/terms" className="underline underline-offset-2 hover:text-foreground">Terms</Link>,{" "}
              <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>, and{" "}
              <Link to="/refunds" className="underline underline-offset-2 hover:text-foreground">Refund Policy</Link>.
            </div>
          </form>

          {!purchaseId && (
            <div className="mt-4 border border-border rounded p-3 bg-secondary/40 text-xs text-muted-foreground">
              You landed here without a purchase reference. If you already paid, your subscription will still link once the webhook arrives — no action needed. Otherwise, <Link to="/pricing" className="text-foreground underline underline-offset-2">pick a plan first</Link>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
